import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useEscaCopy, toEscaLang } from '../i18n/escaCopy';
import { useSEO } from '../hooks/useSEO';
import { initScrollDepthTracking } from '../utils/analytics';
import { escaContacts } from '../data/escaContacts';
import { EscaHeader } from '../components/esca/EscaHeader';
import { EscaHero } from '../components/esca/EscaHero';
import { ScenariosSection } from '../components/esca/ScenariosSection';
import { ExpertiseSection } from '../components/esca/ExpertiseSection';
import { ResearchSection } from '../components/esca/ResearchSection';
import { ProcessSection } from '../components/esca/ProcessSection';
import { ShortlistSection } from '../components/esca/ShortlistSection';
import { CasesSection } from '../components/esca/CasesSection';
import { FounderSection } from '../components/esca/FounderSection';
import { ProofSection } from '../components/esca/ProofSection';
import { GeographySection } from '../components/esca/GeographySection';
import { GuaranteePriceSection } from '../components/esca/GuaranteePriceSection';
import { EscaFaqSection } from '../components/esca/EscaFaqSection';
import { DiscussSection } from '../components/esca/DiscussSection';
import { EscaFooter } from '../components/esca/EscaFooter';
import { StickyCtaBar } from '../components/esca/StickyCtaBar';
const SEO = {
  UA: {
    title: 'ESCA — Executive Search: пошук керівників і рідкісних спеціалістів',
    description: 'Executive Search для C-level, B-level і складних IT-позицій. Досліджуємо ринок і виходимо напряму на кандидатів, які не шукають роботу. 20+ років, 3 500+ закритих вакансій, worldwide.',
    keywords: 'executive search, пошук керівників, хедхантинг, C-level, підбір топ-менеджерів, ESCA'
  },
  EN: {
    title: 'ESCA — Executive Search for leaders and rare specialists',
    description: 'Executive Search for C-level, B-level and hard-to-fill IT roles. We map the market and approach candidates who are not looking for a job. 20+ years, 3,500+ roles filled, worldwide.',
    keywords: 'executive search, headhunting, C-level recruitment, leadership hiring, ESCA'
  }
} as const;
export function EscaLandingPage() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const escaLang = toEscaLang(lang);
  const seo = SEO[escaLang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  useEffect(() => initScrollDepthTracking(), []);
  useEffect(() => {
    const id = 'esca-jsonld';
    document.getElementById(id)?.remove();
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify([{
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'ESCA',
      description: seo.description,
      email: escaContacts.email,
      telephone: escaContacts.phones[0],
      areaServed: c.geography.regions,
      sameAs: [escaContacts.linkedin, escaContacts.telegram],
      founder: {
        '@type': 'Person',
        name: c.founder.name,
        jobTitle: c.founder.role
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: c.faq.items.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    }]);
    document.head.appendChild(script);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [c, seo.description]);
  return <div className="min-h-screen w-full bg-white overflow-x-hidden font-sans antialiased" data-id="element-1546" data-mp="10bp48obq46q4">
      <EscaHeader data-id="element-1547" data-mp="15aa58pysuufm" />
      <main data-id="element-1548" data-mp="159qmj71bs20j">
        <EscaHero data-id="element-1549" data-mp="15do7zzbdl5nl" />
        <ScenariosSection data-id="element-1550" data-mp="3l2pm2yy1sw8v" />
        <ExpertiseSection data-id="element-1551" data-mp="3l2g3hwfc1rd7" />
        <ResearchSection data-id="element-1552" data-mp="3l165t8ce6l73" />
        <ProcessSection data-id="element-1553" data-mp="3l52d01lsagy2" />
        <ShortlistSection data-id="element-1554" data-mp="3q2by552546g3" />
        <CasesSection data-id="element-1555" data-mp="3q2vjywuwveze" />
        <FounderSection data-id="element-1556" data-mp="3q33wgnn4gia2" />
        <ProofSection data-id="element-1557" data-mp="a3km6j7iv5va" />
        <GeographySection data-id="element-1558" data-mp="a42u4d3jr8j1" />
        <GuaranteePriceSection data-id="element-1559" data-mp="a4ci5v5175m3" />
        <EscaFaqSection data-id="element-1560" data-mp="a5nj7n73dz9i" />
        <DiscussSection data-id="element-1561" data-mp="5d48qz3pbk8x" />
      </main>
      <EscaFooter data-id="element-1562" data-mp="5c2mdyh8jbbw" />
      <StickyCtaBar data-id="element-1563" data-mp="5bjfdct4i7cn" />
    </div>;
}